import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { getWithExpiry } from '../../../Domain/Helpers/LocalStorageHelper';
import { getAggregators } from '../../../Infrastructure/Services/AggregatorService';
import { getDbProviders, getSimilarProviderNames } from '../../../Infrastructure/Services/ProviderService';
import ProviderPairSelector from '../../../Domain/Components/UI/ProviderPairSelector/ProviderPairSelector';
import {
  getEnabledCasinos,
  getCasinoProviders,
  getCasinoCategories,
  getCasinoJurisdictions,
  getCasinoIntegrators,
  getCasinoGameList,
  importCasinoGames,
  getGameThumbnails,
  importGameThumbnails,
  getCategoryBonusTags
} from '../../../Infrastructure/Services/CasinoGameService';
import '../../App.css';

/* ─────────────────────────────────────────────────────────────
   TTL cache — 15 min
───────────────────────────────────────────────────────────────*/
const TTL = 15 * 60 * 1000;
const cacheStore = {};
const cacheGet  = (k) => { const e = cacheStore[k]; return e && Date.now() < e.exp ? e.val : null; };
const cacheSet  = (k, v) => { cacheStore[k] = { val: v, exp: Date.now() + TTL }; };

/* ─── Enums ─── */
const PLATFORMS    = ['DESKTOP', 'MOBILE', 'BOTH'];
const ASPECT_RATIOS = ['R_16_9', 'R_4_3', 'R_3_4', 'R_9_16'];

/* ─── Today ISO ─── */
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T00:00:00Z`;
};

/* ─── Shared UI primitives ─── */
function FilterGroup({ label, children }) {
  return (
    <div className="filter-group">
      <label className="filter-label">{label}</label>
      {children}
    </div>
  );
}
function FSelect({ value, onChange, disabled, children, style }) {
  return (
    <select className="form-select form-select-sm" style={{ minWidth: 170, ...style }}
      value={value} onChange={e => onChange(e.target.value)} disabled={disabled}>
      {children}
    </select>
  );
}
function StatusBadge({ yes, yesLabel = 'Yes', noLabel = 'No' }) {
  return yes
    ? <span className="badge bg-success">{yesLabel}</span>
    : <span className="badge bg-secondary">{noLabel}</span>;
}

/* ─── Import Result Modal ─── */
function ImportResultModal({ result, onClose }) {
  if (!result) return null;
  const { created = 0, activated = 0, skipped = 0, errors = [] } = result;
  return (
    <div style={{ position:'fixed', inset:0, zIndex:2000, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center' }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:10, padding:28, minWidth:420, maxWidth:600,
        width:'90%', boxShadow:'0 8px 32px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 style={{ margin:0, fontWeight:700, color:'#2e2e31' }}>Import Result</h5>
          <button style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#888', lineHeight:1 }} onClick={onClose}>×</button>
        </div>
        <div style={{ display:'flex', gap:12, marginBottom:20 }}>
          {[['#d1fae5','#065f46',created,'Created'],['#dbeafe','#1e3a8a',activated,'Activated'],
            ['#fef3c7','#92400e',skipped,'Skipped']].map(([bg,color,val,label]) => (
            <div key={label} style={{ flex:1, background:bg, color, borderRadius:8, padding:'12px 8px', textAlign:'center' }}>
              <div style={{ fontSize:26, fontWeight:800 }}>{val}</div>
              <div style={{ fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>{label}</div>
            </div>
          ))}
        </div>
        {errors.length > 0 && (
          <>
            <p style={{ fontSize:12, fontWeight:700, color:'#92400e', marginBottom:6, textTransform:'uppercase' }}>Errors</p>
            <div style={{ maxHeight:200, overflowY:'auto', border:'1px solid #fde68a', borderRadius:6, background:'#fffbeb', padding:'6px 0' }}>
              {errors.map((msg, i) => (
                <div key={i} style={{ padding:'5px 12px', fontSize:12, color:'#78350f',
                  borderBottom: i < errors.length-1 ? '1px solid #fde68a' : 'none' }}>{msg}</div>
              ))}
            </div>
          </>
        )}
        <div className="d-flex justify-content-end mt-3">
          <button className="btn btn-fadeblack btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Confirm Modal ─── */
function ConfirmModal({ title, children, confirmLabel = 'Confirm', onConfirm, onCancel, confirming }) {
  return (
    <div style={{ position:'fixed', inset:0, zIndex:2500, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center' }} onClick={onCancel}>
      <div style={{ background:'#fff', borderRadius:10, padding:24, minWidth:380, maxWidth:520,
        width:'90%', boxShadow:'0 8px 32px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
        <h5 style={{ margin:'0 0 12px', fontWeight:700, color:'#2e2e31' }}>{title}</h5>
        <div style={{ fontSize:13, color:'#374151', marginBottom:20 }}>{children}</div>
        <div className="d-flex justify-content-end gap-2">
          <button className="btn btn-outline-secondary btn-sm" onClick={onCancel} disabled={confirming}>Cancel</button>
          <button className="btn btn-flexpink btn-sm" onClick={onConfirm} disabled={confirming}>
            {confirming ? <><span className="spinner-border spinner-border-sm me-1" />Working…</> : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Searchable dropdown ─── */
function SearchableSelect({ value, onChange, disabled, options, placeholder = '— Select —', labelKey = 'label', valueKey = 'value' }) {
  const [search, setSearch] = React.useState('');
  const [open, setOpen]     = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Reset search when closed
  React.useEffect(() => { if (!open) setSearch(''); }, [open]);

  const filtered = options.filter(o =>
    String(o[labelKey]).toLowerCase().includes(search.toLowerCase())
  );

  const selectedLabel = options.find(o => String(o[valueKey]) === String(value))?.[labelKey];

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: 180 }}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        style={{
          width: '100%', padding: '5px 30px 5px 10px', fontSize: 13,
          border: '1px solid #dee2e6', borderRadius: 4,
          background: disabled ? '#e9ecef' : '#fff',
          color: value ? '#2e2e31' : '#6c757d',
          cursor: disabled ? 'default' : 'pointer',
          textAlign: 'left', position: 'relative',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedLabel ?? placeholder}
        </span>
        <span style={{ fontSize: 10, color: '#6c757d', flexShrink: 0, marginLeft: 6 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: '#fff', border: '1px solid #dee2e6', borderRadius: 4,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', marginTop: 2,
          minWidth: 220,
        }}>
          {/* Search input */}
          <div style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
            <input
              autoFocus
              type="text"
              className="form-control form-control-sm"
              placeholder="Search…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
              style={{ fontSize: 12 }}
            />
          </div>
          {/* Options */}
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            <div
              onClick={() => { onChange(''); setOpen(false); }}
              style={{
                padding: '6px 12px', fontSize: 13, cursor: 'pointer',
                color: '#6c757d',
                background: !value ? '#f8f9fa' : 'transparent',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
              onMouseLeave={e => e.currentTarget.style.background = !value ? '#f8f9fa' : 'transparent'}
            >
              {placeholder}
            </div>
            {filtered.length === 0 && (
              <div style={{ padding: '8px 12px', fontSize: 12, color: '#9ca3af' }}>No results</div>
            )}
            {filtered.map(o => (
              <div
                key={o[valueKey]}
                onClick={() => { onChange(String(o[valueKey])); setOpen(false); }}
                style={{
                  padding: '6px 12px', fontSize: 13, cursor: 'pointer',
                  background: String(o[valueKey]) === String(value) ? '#fdf2f8' : 'transparent',
                  color: String(o[valueKey]) === String(value) ? '#e20075' : '#2e2e31',
                  fontWeight: String(o[valueKey]) === String(value) ? 600 : 400,
                }}
                onMouseEnter={e => { if (String(o[valueKey]) !== String(value)) e.currentTarget.style.background = '#f8f9fa'; }}
                onMouseLeave={e => { e.currentTarget.style.background = String(o[valueKey]) === String(value) ? '#fdf2f8' : 'transparent'; }}
              >
                {o[labelKey]}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SHARED FILTER BAR — used by both Browse and Import tabs
═══════════════════════════════════════════════════════════════ */
function FilterBar({
  aggregators, gisProviders, enabledCasinos, casinoProviders, filteredCasinoProviders,
  loadingAggs, loadingGisProviders, loadingCasinos, loadingCasinoProviders,
  aggId, setAggId, providerId, apiProviderName, onProviderPairChange,
  casinoId, setCasinoId, casinoProviderKey, setCasinoProviderKey,
  strictNameMatch, setStrictNameMatch,
  namesMismatch, onSearch, loadingGames,
  token, setLoggedIn,
}) {
  return (
    <div className="card-section">
      <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>

        <FilterGroup label="Aggregator">
          {loadingAggs
            ? <div className="spinner-border spinner-border-sm text-secondary" role="status" />
            : <FSelect value={aggId} onChange={v => { setAggId(v); }}>
                <option value="">— Select —</option>
                {aggregators.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </FSelect>
          }
        </FilterGroup>

        <ProviderPairSelector
          aggId={aggId}
          dbProviders={gisProviders}
          loadingDbProviders={loadingGisProviders}
          providerId={providerId}
          apiProviderName={apiProviderName}
          onChange={onProviderPairChange}
          token={token}
          setLoggedIn={setLoggedIn}
        />

        <FilterGroup label="Casino">
          {loadingCasinos
            ? <div className="spinner-border spinner-border-sm text-secondary" role="status" />
            : <FSelect value={casinoId} onChange={v => { setCasinoId(v); }}>
                <option value="">— Select —</option>
                {enabledCasinos.map(c => (
                  <option key={c.id} value={c.id}>{c.company_name} ({c.brand})</option>
                ))}
              </FSelect>
          }
        </FilterGroup>

        <div className="filter-group">
          <label className="filter-label">Casino Provider</label>
          {loadingCasinoProviders
            ? <div className="spinner-border spinner-border-sm text-secondary" role="status" />
            : <SearchableSelect
                value={casinoProviderKey}
                onChange={v => { setCasinoProviderKey(v); }}
                disabled={!casinoId}
                options={filteredCasinoProviders.map(p => ({ value: p.key, label: `${p.title} (${p.key})` }))}
                placeholder="— Select —"
              />
          }
          <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:11,
            color:'#6b7280', marginTop:5, cursor:'pointer', userSelect:'none' }}>
            <input type="checkbox" checked={strictNameMatch}
              onChange={e => { setStrictNameMatch(e.target.checked); setCasinoProviderKey(''); }} />
            Match casino provider by GIS name
          </label>
          {!strictNameMatch && namesMismatch && casinoProviderKey && (
            <p style={{ fontSize:10, color:'#d97706', margin:'3px 0 0', fontStyle:'italic' }}>
              Casino provider name doesn't match GIS provider
            </p>
          )}
          {strictNameMatch && providerId && casinoId && !loadingCasinoProviders && filteredCasinoProviders.length === 0 && (
            <p style={{ fontSize:10, color:'#ef4444', margin:'3px 0 0', fontStyle:'italic' }}>
              No matching provider found — uncheck to see all
            </p>
          )}
        </div>

        <div className="filter-group">
          <label className="filter-label">&nbsp;</label>
          <button className="btn btn-fadeblack btn-sm" onClick={onSearch}
            disabled={loadingGames || !aggId || !providerId || !casinoId || !casinoProviderKey}>
            {loadingGames
              ? <><span className="spinner-border spinner-border-sm me-1" />Searching…</>
              : 'Search Games'
            }
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   TAB 1 — Browse Games
═══════════════════════════════════════════════════════════════ */
function BrowseTab({ games, loadingGames, selectedCasino }) {
  if (!games.length && !loadingGames) {
    return (
      <div className="card-section">
        <div className="empty-state">
          <p>Fill in all filters and click Search Games</p>
        </div>
      </div>
    );
  }
  return (
    <div className="card-section">
      {loadingGames
        ? <div className="text-center p-4"><div className="spinner-border text-secondary" role="status" /></div>
        : <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0" style={{ color:'#2e2e31' }}>
                {games.length} games
                {selectedCasino && (
                  <span style={{ fontWeight:400, fontSize:13, color:'#607d8b', marginLeft:8 }}>
                    — {selectedCasino.company_name}
                  </span>
                )}
              </h6>
            </div>
            <div style={{ overflowX:'auto' }}>
              <table className="table table-hover table-sm align-middle">
                <thead>
                  <tr>
                    <th>Game Name</th>
                    <th>Provider Game ID</th>
                    <th>BW Game Code</th>
                    <th>External Game ID</th>
                    <th>Game Config</th>
                    <th>In BW</th>
                    <th>Active BW</th>
                    <th>In Casino</th>
                    <th>Enabled</th>
                    <th>Lobby</th>
                  </tr>
                </thead>
                <tbody>
                  {games.map((g, i) => {
                    const bwCode = (() => {
                      if (!g.internalGameId) return null;
                      const idx = g.internalGameId.indexOf('-flx-');
                      return idx !== -1 ? g.internalGameId.slice(idx + 1) : g.internalGameId;
                    })();
                    return (
                      <tr key={g.gameConfigId ?? i}>
                        <td><strong style={{ fontSize:13 }}>{g.name}</strong></td>
                        <td><code style={{ fontSize:11 }}>{g.providerGameId ?? '—'}</code></td>
                        <td>{bwCode ? <code style={{ fontSize:11 }}>{bwCode}</code> : <span className="text-muted">—</span>}</td>
                        <td>{g.externalGameId ? <code style={{ fontSize:11 }}>{g.externalGameId}</code> : <span className="text-muted">—</span>}</td>
                        <td><StatusBadge yes={g.isActiveGameConfig} yesLabel="Active" noLabel="Inactive" /></td>
                        <td><StatusBadge yes={g.isAddedBw} /></td>
                        <td><StatusBadge yes={g.isActiveBw} /></td>
                        <td><StatusBadge yes={g.isAddedCasino} /></td>
                        <td>{g.enabled !== null && g.enabled !== undefined ? <StatusBadge yes={g.enabled} /> : <span className="text-muted">—</span>}</td>
                        <td>{g.showInLobby !== null && g.showInLobby !== undefined ? <StatusBadge yes={g.showInLobby} /> : <span className="text-muted">—</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
      }
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   TAB 2 — Import Games
═══════════════════════════════════════════════════════════════ */

/* ─── Bonus tag auto-fill ─── */
const splitTags = (tags) => (tags ? tags.split(',').map(t => t.trim()).filter(Boolean) : []);

const bonusTagsFor = (categoryKeys = [], bonusTags = {}) =>
  [...new Set(categoryKeys.map(key => bonusTags[key]).filter(Boolean))];

const syncBonusTags = (tags, prevCategories, nextCategories, bonusTags) => {
  const prevBonus = bonusTagsFor(prevCategories, bonusTags);
  const nextBonus = bonusTagsFor(nextCategories, bonusTags);
  const manual    = splitTags(tags).filter(t => !prevBonus.includes(t));

  return [...new Set([...manual, ...nextBonus])].join(', ');
};

const blankRow = () => ({
  jurisdiction:   '',
  integrationKey: '',
  categories:     [],
  platform:       'BOTH',
  aspectRatio:    'R_16_9',
  description:    '',
  tags:           '',
  realMode:       true,
  funMode:        false,
  showInLobby:    true,
  releaseDate:    todayISO(),
});

function CellSelect({ value, onChange, options, placeholder }) {
  return (
    <select className="form-select form-select-sm" style={{ minWidth: 110, fontSize: 11 }}
      value={value} onChange={e => onChange(e.target.value)}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}
function CellInput({ value, onChange, placeholder, style }) {
  return (
    <input type="text" className="form-control form-control-sm"
      style={{ minWidth: 100, fontSize: 11, ...style }}
      value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
  );
}
function CellBool({ value, onChange }) {
  return (
    <div style={{ display:'flex', gap: 6, fontSize: 11 }}>
      {[true, false].map(b => (
        <label key={String(b)} style={{ display:'flex', alignItems:'center', gap:3, cursor:'pointer' }}>
          <input type="radio" checked={value === b} onChange={() => onChange(b)} />
          {b ? 'Yes' : 'No'}
        </label>
      ))}
    </div>
  );
}
function CellDate({ value, onChange }) {
  const display = value ? value.slice(0, 10) : '';
  return (
    <input type="date" className="form-control form-control-sm" style={{ minWidth: 120, fontSize: 11 }}
      value={display}
      onChange={e => onChange(e.target.value ? `${e.target.value}T00:00:00Z` : '')} />
  );
}
function CellMultiSelect({ value = [], onChange, options }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (key) => {
    const next = value.includes(key)
      ? value.filter(k => k !== key)
      : [...value, key];
    onChange(next);
  };

  const label = value.length === 0
    ? 'None'
    : value.length === 1
      ? value[0]
      : `${value.length} selected`;

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: 130 }}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', fontSize: 11, padding: '3px 8px',
          border: '1px solid #dee2e6', borderRadius: 4,
          background: value.length > 0 ? '#fdf2f8' : '#fff',
          color: value.length > 0 ? '#e20075' : '#6b7280',
          cursor: 'pointer', textAlign: 'left',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 90 }}>
          {label}
        </span>
        <span style={{ fontSize: 9, marginLeft: 4 }}>{open ? '▲' : '▼'}</span>
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 100,
          background: '#fff', border: '1px solid #dee2e6', borderRadius: 4,
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          minWidth: 160, maxHeight: 200, overflowY: 'auto', marginTop: 2,
        }}>
          {options.length === 0 && (
            <span style={{ fontSize: 11, color: '#9ca3af', padding: '6px 10px', display: 'block' }}>
              No categories
            </span>
          )}
          {options.map(o => (
            <label key={o.key} style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '5px 10px', cursor: 'pointer', fontSize: 11,
              background: value.includes(o.key) ? '#fdf2f8' : 'transparent',
              borderBottom: '1px solid #f3f4f6',
            }}>
              <input
                type="checkbox"
                checked={value.includes(o.key)}
                onChange={() => toggle(o.key)}
                style={{ margin: 0 }}
              />
              {o.key}
            </label>
          ))}
          {value.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              style={{
                width: '100%', fontSize: 10, padding: '5px 10px',
                border: 'none', borderTop: '1px solid #e5e7eb',
                background: '#f9fafb', color: '#6b7280', cursor: 'pointer', textAlign: 'left',
              }}
            >
              Clear all
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Import table with mirrored top scrollbar + sticky header rows ─── */
const isEligible = (g) =>
  !g.isAddedCasino &&
  g.operatorGameConfigId !== null &&
  g.isAddedBw === true &&
  g.isActiveBw === true;

function ImportTableWithTopScroll({ COLS, allGames, importableGames, selected, allChecked, toggleAll, toggleSelect, applyHeaderToSelected }) {
  const topBarRef  = useRef(null);
  const tableWrapRef = useRef(null);
  const innerRef   = useRef(null);

  // Sync scroll between top mirror bar and table wrapper
  const onTopScroll = () => {
    if (tableWrapRef.current) tableWrapRef.current.scrollLeft = topBarRef.current.scrollLeft;
  };
  const onTableScroll = () => {
    if (topBarRef.current) topBarRef.current.scrollLeft = tableWrapRef.current.scrollLeft;
  };

  // Match mirror width to table width
  useEffect(() => {
    if (!innerRef.current || !topBarRef.current) return;
    const ro = new ResizeObserver(() => {
      if (innerRef.current && topBarRef.current) {
        topBarRef.current.firstChild.style.width = innerRef.current.scrollWidth + 'px';
      }
    });
    ro.observe(innerRef.current);
    return () => ro.disconnect();
  }, []);

  return (
    <>
      {/* ── Top scrollbar mirror ── */}
      <div
        ref={topBarRef}
        onScroll={onTopScroll}
        className="import-table-top-scroll"
      >
        <div style={{ height: 1 }} />
      </div>

      {/* ── Main scrollable table ── */}
      <div
        ref={tableWrapRef}
        onScroll={onTableScroll}
        className="import-table-wrap"
      >
        <table
          ref={innerRef}
          className="table table-sm align-middle"
          style={{ fontSize:14, marginBottom:0, minWidth:1600, tableLayout:'auto' }}
        >
          <thead>
            {/* Row 1: Apply buttons — sticky row 1 */}
            <tr style={{ background:'#f8fafc', borderBottom:'2px solid #e2e8f0' }}>
              <th colSpan={3} style={{ padding:'8px 12px', fontSize:10, color:'#94a3b8', fontWeight:400, whiteSpace:'nowrap', background:'#f8fafc' }}>
                Apply defaults ↓
              </th>
              {COLS.map(col => (
                <th key={col.key} style={{ padding:'6px 8px', textAlign:'center', background:'#f8fafc' }}>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    style={{ fontSize:10, padding:'2px 8px', whiteSpace:'nowrap' }}
                    title={`Apply ${col.label} to ${selected.size > 0 ? 'selected' : 'all'} rows`}
                    onClick={() => applyHeaderToSelected(col.key)}
                  >
                    Apply
                  </button>
                </th>
              ))}
            </tr>
            {/* Row 2: Default value inputs — sticky row 2 */}
            <tr style={{ background:'#f0f9ff', borderBottom:'2px solid #bae6fd' }}>
              <th style={{ padding:'8px 12px', fontSize:11, color:'#0369a1', fontWeight:700, whiteSpace:'nowrap', background:'#f0f9ff' }}>
                Default values
              </th>
              <th style={{ background:'#f0f9ff' }} /><th style={{ background:'#f0f9ff' }} />
              {COLS.map(col => (
                <th key={col.key} style={{ padding:'6px 8px', background:'#f0f9ff' }}>
                  {col.header()}
                </th>
              ))}
            </tr>
            {/* Row 3: Column labels — sticky row 3 */}
            <tr style={{ background:'#fff' }}>
              <th style={{ width:36, background:'#fff' }}>
                <input type="checkbox" checked={allChecked} onChange={toggleAll} title="Select all importable" />
              </th>
              <th style={{ background:'#fff' }}>Game Name</th>
              <th style={{ background:'#fff' }}>Provider Game ID</th>
              {COLS.map(col => (
                <th key={col.key} style={{ whiteSpace:'nowrap', background:'#fff' }}>{col.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allGames.map(g => {
              const id       = g.operatorGameConfigId;
              const added    = g.isAddedCasino;
              const eligible = isEligible(g);
              const disabled = !eligible;
              const sel      = eligible && selected.has(id);

              // Reason why a non-added game is disabled
              const disabledReason = added ? null
                : !g.operatorGameConfigId ? 'no operator config'
                : !g.isAddedBw            ? 'not added to BW'
                : !g.isActiveBw           ? 'not active in BW'
                : null;

              return (
                <tr
                  key={id ?? g.providerGameId}
                  style={{
                    background: added    ? '#f0fdf4'
                              : disabled ? '#f9fafb'
                              : sel      ? '#fdf2f8'
                              : undefined,
                    opacity: disabled ? 0.55 : 1,
                    cursor:  eligible  ? 'pointer' : 'default',
                  }}
                  onClick={() => eligible && toggleSelect(id)}
                >
                  <td onClick={e => e.stopPropagation()}>
                    {added
                      ? <span className="badge bg-success" style={{ fontSize:10 }}>Added</span>
                      : disabled
                        ? <span className="badge bg-secondary" style={{ fontSize:10 }}>N/A</span>
                        : <input type="checkbox" checked={sel} onChange={() => toggleSelect(id)} />
                    }
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <strong style={{ fontSize:14 }}>{g.name}</strong>
                    {added && (
                      <span style={{ fontSize:10, color:'#10b981', marginLeft:6, fontWeight:600 }}>
                        ✓ in casino
                      </span>
                    )}
                    {disabledReason && (
                      <span style={{ fontSize:10, color:'#9ca3af', marginLeft:6 }}>
                        {disabledReason}
                      </span>
                    )}
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <code style={{ fontSize:11 }}>{g.providerGameId ?? '—'}</code>
                  </td>
                  {COLS.map(col => (
                    <td key={col.key} onClick={e => e.stopPropagation()}>
                      {added ? <span className="text-muted">—</span> : col.cell(id)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ImportTab({ games, loadingGames, casinoId, casinoProviderKey, jurisdictions, integrators, categories, bonusTags, enabledCasinos, setLoggedIn, onReload, aggId, providerId, apiProviderName, dbProviderName }) {
  // All games shown; already-added or ineligible ones are read-only
  const allGames        = games;
  const importableGames = games.filter(isEligible);
  const alreadyAdded    = games.filter(g => g.isAddedCasino);

  const [rows, setRows] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [header, setHeader] = useState(blankRow());
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [similarWarnings, setSimilarWarnings] = useState([]);
  const [checkingSimilar, setCheckingSimilar] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);

  useEffect(() => {
    const init = {};
    importableGames.forEach(g => {
      const id = g.operatorGameConfigId;
      init[id] = rows[id] ?? {
        ...blankRow(),
        integrationKey: g.integrationKey ?? '',
      };
    });
    setRows(init);
    setSelected(new Set());
  }, [games]);

  const withBonusTags = (row, field, value) => {
  const next = { ...row, [field]: value };
  if (field !== 'categories') return next;

    next.tags = syncBonusTags(row.tags, row.categories, value, bonusTags);
    return next;
  };

  const updateRow = (id, field, value) =>
    setRows(prev => ({ ...prev, [id]: withBonusTags(prev[id], field, value) }));

  const updateHeader = (field, value) =>
    setHeader(prev => withBonusTags(prev, field, value));

  // Deep-copy arrays to avoid shared references between rows
  const applyHeaderToSelected = (field) => {
    const targets = selected.size > 0 ? [...selected] : importableGames.map(g => g.operatorGameConfigId);
    const headerVal = header[field];
    setRows(prev => {
      const next = { ...prev };
      targets.forEach(id => {
        next[id] = withBonusTags(next[id], field, Array.isArray(headerVal) ? [...headerVal] : headerVal);
      });
      return next;
    });
  };

  const toggleSelect = (id) =>
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () =>
    setSelected(prev => prev.size === importableGames.length
      ? new Set()
      : new Set(importableGames.map(g => g.operatorGameConfigId)));

  const allChecked = selected.size === importableGames.length && importableGames.length > 0;

  const openImportConfirm = async () => {
    const targets = selected.size > 0 ? [...selected] : importableGames.map(g => g.operatorGameConfigId);
    if (!targets.length) { toast.warn('No games to import'); return; }

    for (const id of targets) {
      const r = rows[id];
      if (!r?.jurisdiction) { toast.error('Jurisdiction is required for all games'); return; }
      if (!r?.integrationKey) { toast.error('Integration Key is required for all games'); return; }
      if (!r?.releaseDate) { toast.error('Release Date is required for all games'); return; }
      if (!r?.categories?.length) { toast.error('At least one Category is required for all games'); return; }
    }

    const gamesPayload = targets.map(id => {
      const r = rows[id];
      return {
        operatorGameConfigId: id,
        providerKey:    casinoProviderKey,
        integrationKey: r.integrationKey,
        jurisdiction:   r.jurisdiction,
        platform:       r.platform,
        aspectRatio:    r.aspectRatio,
        description:    r.description || null,
        tags:           r.tags
          ? [...new Set(r.tags.split(',').map(tag => tag.trim()).filter(Boolean))]
          : [],
        realMode:       r.realMode,
        funMode:        r.funMode,
        showInLobby:    r.showInLobby,
        releaseDate:    r.releaseDate,
        categoryKeys:   r.categories,
      };
    });

    setPendingPayload(gamesPayload);
    setShowImportConfirm(true);

    const token = getWithExpiry('token');
    if (!token) { setLoggedIn(false); return; }
    setCheckingSimilar(true);
    try {
      const result = await getSimilarProviderNames(token, aggId, providerId, apiProviderName, dbProviderName);
      setSimilarWarnings(result ?? []);
    } finally { setCheckingSimilar(false); }
  };

  const handleImport = async () => {
    if (!pendingPayload) return;
    const token = getWithExpiry('token');
    if (!token) { setLoggedIn(false); return; }

    setImporting(true);
    try {
      const result = await importCasinoGames(token, { casinoId: Number(casinoId), games: pendingPayload });
      if (result) {
        setImportResult(result);
      }
      setShowImportConfirm(false);
      setPendingPayload(null);
    } catch { toast.error('Import failed'); }
    finally { setImporting(false); }
  };

  if (loadingGames) {
    return <div className="card-section text-center p-4"><div className="spinner-border text-secondary" role="status" /></div>;
  }

  if (!games.length) {
    return (
      <div className="card-section">
        <div className="empty-state"><p>Search for games using the filters above first</p></div>
      </div>
    );
  }

  const selectedCasino = enabledCasinos.find(c => String(c.id) === String(casinoId));

  const COLS = [
    { key: 'jurisdiction',   label: 'Jurisdiction *',
      header: () => <FSelect value={header.jurisdiction} onChange={v => updateHeader('jurisdiction', v)} style={{ minWidth:110, fontSize:11 }}>
        <option value="">— Select —</option>
        {jurisdictions.map(j => <option key={j} value={j}>{j}</option>)}
      </FSelect>,
      cell: (id) => <FSelect value={rows[id]?.jurisdiction ?? ''} onChange={v => updateRow(id, 'jurisdiction', v)} style={{ minWidth:110, fontSize:11 }}>
        <option value="">— Select —</option>
        {jurisdictions.map(j => <option key={j} value={j}>{j}</option>)}
      </FSelect>,
    },
    { key: 'integrationKey', label: 'Integration Key *',
      header: () => <FSelect value={header.integrationKey} onChange={v => updateHeader('integrationKey', v)} style={{ minWidth:120, fontSize:11 }}>
        <option value="">— Select —</option>
        {integrators.map(i => <option key={i} value={i}>{i}</option>)}
      </FSelect>,
      cell: (id) => <FSelect value={rows[id]?.integrationKey ?? ''} onChange={v => updateRow(id, 'integrationKey', v)} style={{ minWidth:120, fontSize:11 }}>
        <option value="">— Select —</option>
        {integrators.map(i => <option key={i} value={i}>{i}</option>)}
      </FSelect>,
    },
    { key: 'platform', label: 'Platform *',
      header: () => <CellSelect value={header.platform} onChange={v => updateHeader('platform', v)} options={PLATFORMS} />,
      cell: (id) => <CellSelect value={rows[id]?.platform ?? 'BOTH'} onChange={v => updateRow(id, 'platform', v)} options={PLATFORMS} />,
    },
    { key: 'aspectRatio', label: 'Aspect Ratio *',
      header: () => <CellSelect value={header.aspectRatio} onChange={v => updateHeader('aspectRatio', v)} options={ASPECT_RATIOS} />,
      cell: (id) => <CellSelect value={rows[id]?.aspectRatio ?? 'R_16_9'} onChange={v => updateRow(id, 'aspectRatio', v)} options={ASPECT_RATIOS} />,
    },
    { key: 'realMode', label: 'Real Mode *',
      header: () => <CellBool value={header.realMode} onChange={v => updateHeader('realMode', v)} />,
      cell: (id) => <CellBool value={rows[id]?.realMode ?? true} onChange={v => updateRow(id, 'realMode', v)} />,
    },
    { key: 'funMode', label: 'Fun Mode *',
      header: () => <CellBool value={header.funMode} onChange={v => updateHeader('funMode', v)} />,
      cell: (id) => <CellBool value={rows[id]?.funMode ?? false} onChange={v => updateRow(id, 'funMode', v)} />,
    },
    { key: 'showInLobby', label: 'Show in Lobby *',
      header: () => <CellBool value={header.showInLobby} onChange={v => updateHeader('showInLobby', v)} />,
      cell: (id) => <CellBool value={rows[id]?.showInLobby ?? true} onChange={v => updateRow(id, 'showInLobby', v)} />,
    },
    { key: 'releaseDate', label: 'Release Date *',
      header: () => <CellDate value={header.releaseDate} onChange={v => updateHeader('releaseDate', v)} />,
      cell: (id) => <CellDate value={rows[id]?.releaseDate ?? todayISO()} onChange={v => updateRow(id, 'releaseDate', v)} />,
    },
    { key: 'categories', label: 'Categories *',
      header: () => <CellMultiSelect value={header.categories} onChange={v => updateHeader('categories', v)} options={categories} />,
      cell: (id) => <CellMultiSelect value={rows[id]?.categories ?? []} onChange={v => updateRow(id, 'categories', v)} options={categories} />,
    },
    { key: 'description', label: 'Description',
      header: () => <CellInput value={header.description} onChange={v => updateHeader('description', v)} placeholder="Description…" />,
      cell: (id) => <CellInput value={rows[id]?.description ?? ''} onChange={v => updateRow(id, 'description', v)} placeholder="Description…" />,
    },
    { key: 'tags', label: 'Tags',
      header: () => <CellInput value={header.tags} onChange={v => updateHeader('tags', v)} placeholder="bonus,freespins" style={{ minWidth:140 }} />,
      cell: (id) => <CellInput value={rows[id]?.tags ?? ''} onChange={v => updateRow(id, 'tags', v)} placeholder="bonus,freespins" style={{ minWidth:140 }} />,
    },
  ];

  return (
    <>
      {importResult && <ImportResultModal result={importResult} onClose={() => { setImportResult(null); if (onReload) onReload(); }} />}

      {showImportConfirm && (
        <ConfirmModal
          title="Confirm Casino Game Import"
          confirmLabel="Import"
          confirming={importing}
          onCancel={() => { setShowImportConfirm(false); setPendingPayload(null); }}
          onConfirm={handleImport}
        >
          <p>
            Import {pendingPayload?.length ?? 0} game{pendingPayload?.length !== 1 ? 's' : ''} using{' '}
            <strong>{apiProviderName}</strong> → <strong>{dbProviderName}</strong>{' '}
            for <strong>{selectedCasino?.company_name ?? 'this casino'}</strong>?
          </p>
          {checkingSimilar && (
            <div className="d-flex align-items-center gap-2" style={{ fontSize: 12, color: '#6b7280' }}>
              <span className="spinner-border spinner-border-sm" />
              Checking for similar providers…
            </div>
          )}
          {!checkingSimilar && similarWarnings.length > 0 && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 12px', marginTop: 8 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#92400e', margin: '0 0 6px', textTransform: 'uppercase' }}>
                Similar DB Provider Names
              </p>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#78350f' }}>
                {similarWarnings.map(m => (
                  <li key={m.id}>
                    <strong>{m.name}</strong>
                    {m.exactApiNameMatch ? ' — exact match to API name' : ` (${m.score}%)`}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </ConfirmModal>
      )}

      <div className="card-section" style={{ padding:'14px 20px' }}>
        <div className="d-flex justify-content-between align-items-center">
          <div style={{ fontSize:13, color:'#2e2e31' }}>
            <strong>{importableGames.length}</strong> importable
            {alreadyAdded.length > 0 && (
              <span style={{ color:'#9ca3af', marginLeft:8 }}>
                · {alreadyAdded.length} already in casino
              </span>
            )}
            {selected.size > 0 && (
              <span style={{ color:'#e20075', marginLeft:8, fontWeight:600 }}>
                · {selected.size} selected
              </span>
            )}
            {selectedCasino && (
              <span style={{ color:'#607d8b', marginLeft:8 }}>
                → {selectedCasino.company_name}
              </span>
            )}
          </div>
          <button className="btn btn-flexpink btn-sm" onClick={openImportConfirm} disabled={importing || !importableGames.length}>
            {selected.size > 0
              ? `Import Selected (${selected.size})`
              : `Import All (${importableGames.length})`
            }
          </button>
        </div>
      </div>

      <div className="card-section" style={{ padding:0 }}>
        <ImportTableWithTopScroll
          COLS={COLS}
          allGames={allGames}
          importableGames={importableGames}
          selected={selected}
          allChecked={allChecked}
          toggleAll={toggleAll}
          toggleSelect={toggleSelect}
          applyHeaderToSelected={applyHeaderToSelected}
        />
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════
   TAB 3 — Import Thumbnails
═══════════════════════════════════════════════════════════════ */

function ThumbnailPanel({ label, casinoGameId, thumbnails, casinoId, setLoggedIn }) {
  const [selected,  setSelected]  = useState(null);
  const [importing, setImporting] = useState(false);
  const [done,      setDone]      = useState(false);

  const selectThumb = (url) =>
    setSelected(prev => prev === url ? null : url);

  const handleImport = async () => {
    if (!selected) { toast.warn('Select a thumbnail'); return; }
    const token = getWithExpiry('token');
    if (!token) { setLoggedIn(false); return; }
    setImporting(true);
    try {
      const result = await importGameThumbnails(token, {
        casinoId:     Number(casinoId),
        casinoGameId: casinoGameId,
        imagesUrl:    [selected],
      });
      if (result !== null) {
        toast.success(`${label} thumbnail imported`);
        setDone(true);
        setSelected(null);
      }
    } catch { toast.error('Import failed'); }
    finally { setImporting(false); }
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="d-flex align-items-center mb-2">
        <span style={{ fontWeight: 700, fontSize: 12, color: '#374151' }}>
          {label === 'Desktop' ? 'Desktop' : 'Mobile'}
          <span className="badge bg-secondary ms-2" style={{ fontSize: 10 }}>{casinoGameId}</span>
        </span>
      </div>

      {thumbnails.length > 0 && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span style={{ fontSize: 11, color: '#6b7280' }}>
              {thumbnails.length} thumbnails
              {selected && <span style={{ color: '#e20075', marginLeft: 6, fontWeight: 600 }}>· 1 selected</span>}
            </span>
            <button
              className="btn btn-flexpink btn-sm"
              style={{ fontSize: 11 }}
              onClick={handleImport}
              disabled={importing || !selected}
            >
              {importing
                ? <><span className="spinner-border spinner-border-sm me-1" />Importing…</>
                : 'Import Selected'
              }
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8 }}>
            {thumbnails.map((thumb, i) => {
              const sel = selected === thumb.url;
              return (
                <div key={i} onClick={() => selectThumb(thumb.url)} style={{
                  cursor: 'pointer', borderRadius: 7, position: 'relative',
                  border: sel ? '2px solid #e20075' : '2px solid #e5e7eb',
                  overflow: 'hidden', background: '#fff', transition: 'border-color 0.15s',
                }}>
                  <div style={{
                    position: 'absolute', top: 5, right: 5, width: 16, height: 16,
                    borderRadius: '50%', zIndex: 1, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', fontSize: 9, color: '#fff', fontWeight: 700,
                    background: sel ? '#e20075' : 'rgba(255,255,255,0.85)',
                    border: sel ? '2px solid #e20075' : '2px solid #d1d5db',
                  }}>
                    {sel && '✓'}
                  </div>
                  <img src={thumb.url} alt={thumb.label ?? `Thumb ${i+1}`}
                    style={{ width: '100%', height: 80, objectFit: 'cover', display: 'block' }}
                    onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                  <div style={{ display:'none', height:80, alignItems:'center',
                    justifyContent:'center', background:'#f3f4f6', fontSize:18 }}></div>
                  <div style={{ padding:'4px 6px', fontSize:10, color:'#6b7280',
                    borderTop:'1px solid #f3f4f6', background: sel ? '#fff0f5':'#fff',
                    textOverflow:'ellipsis', overflow:'hidden', whiteSpace:'nowrap' }}>
                    {thumb.label ?? `Thumbnail ${i+1}`}
                  </div>
                </div>
              );
            })}
          </div>

          {done && (
            <p style={{ fontSize: 11, color: '#059669', marginTop: 8, marginBottom: 0 }}>
              {label} thumbnail imported successfully
            </p>
          )}
        </>
      )}
    </div>
  );
}

function ThumbnailGameRow({ game, aggId, casinoId, setLoggedIn }) {
  const [expanded,   setExpanded]   = useState(false);
  const [thumbnails, setThumbnails] = useState([]);
  const [loading,    setLoading]    = useState(false);

  const desktopId = game.casino?.desktop?.id ?? null;
  const mobileId  = game.casino?.mobile?.id  ?? null;

  const bwCode = (() => {
    if (!game.internalGameId) return null;
    const idx = game.internalGameId.indexOf('-flx-');
    return idx !== -1 ? game.internalGameId.slice(idx + 1) : game.internalGameId;
  })();

  const loadThumbnails = async () => {
    const token = getWithExpiry('token');
    if (!token) { setLoggedIn(false); return; }
    setLoading(true);
    try {
      const result = await getGameThumbnails(token, aggId, game.operatorGameConfigId);
      setThumbnails(Array.isArray(result) ? result : []);
      if (!result?.length) toast.info(`No thumbnails found for ${game.name}`);
    } catch { toast.error('Failed to load thumbnails'); }
    finally { setLoading(false); }
  };

  return (
    <>
      <tr style={{ background: expanded ? '#f0f9ff' : undefined }}>
        <td><strong style={{ fontSize: 13 }}>{game.name}</strong></td>
        <td><code style={{ fontSize: 11 }}>{game.providerGameId ?? '—'}</code></td>
        <td>{bwCode ? <code style={{ fontSize: 11 }}>{bwCode}</code> : <span className="text-muted">—</span>}</td>
        <td>{game.externalGameId ? <code style={{ fontSize: 11 }}>{game.externalGameId}</code> : <span className="text-muted">—</span>}</td>
        <td>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {desktopId && <span><span style={{ fontSize: 10, color: '#6b7280' }}>Desktop </span><span className="badge bg-secondary">{desktopId}</span></span>}
            {mobileId  && <span><span style={{ fontSize: 10, color: '#6b7280' }}>Mobile </span><span className="badge bg-secondary">{mobileId}</span></span>}
            {!desktopId && !mobileId && <span className="text-muted">—</span>}
          </div>
        </td>
        <td>
          <div className="d-flex gap-2">
            <button
              className={`btn btn-sm ${expanded ? 'btn-fadeblack' : 'btn-outline-secondary'}`}
              onClick={() => setExpanded(e => !e)}
              style={{ whiteSpace: 'nowrap' }}
            >
              {expanded ? 'Hide' : 'Thumbnails'}
            </button>
            {expanded && (
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={loadThumbnails}
                disabled={loading}
                style={{ whiteSpace: 'nowrap' }}
              >
                {loading
                  ? <><span className="spinner-border spinner-border-sm me-1" />Loading…</>
                  : thumbnails.length > 0 ? 'Reload' : 'Load'
                }
              </button>
            )}
          </div>
        </td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={6} style={{ background: '#f8fafc', padding: '16px 20px', borderBottom: '2px solid #bae6fd' }}>
            {!loading && thumbnails.length === 0 && (
              <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
                Click <strong>Load</strong> to fetch thumbnails — the same image set will appear in both Desktop and Mobile panels so you can select different images for each.
              </p>
            )}
            {loading && (
              <div className="text-center p-3">
                <div className="spinner-border text-secondary" role="status" />
              </div>
            )}
            {!loading && thumbnails.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: desktopId && mobileId ? '1fr 1fr' : '1fr', gap: 24 }}>
                {desktopId && (
                  <ThumbnailPanel
                    label="Desktop"
                    casinoGameId={desktopId}
                    thumbnails={thumbnails}
                    casinoId={casinoId}
                    setLoggedIn={setLoggedIn}
                  />
                )}
                {mobileId && (
                  <ThumbnailPanel
                    label="Mobile"
                    casinoGameId={mobileId}
                    thumbnails={thumbnails}
                    casinoId={casinoId}
                    setLoggedIn={setLoggedIn}
                  />
                )}
              </div>
            )}
            {!desktopId && !mobileId && !loading && (
              <p className="text-muted mb-0" style={{ fontSize: 13 }}>No casino platform entries for this game.</p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function ImportThumbnailsTab({ games, loadingGames, aggId, casinoId, setLoggedIn }) {
  const casinoGames = games.filter(g =>
    g.casino?.desktop?.id != null || g.casino?.mobile?.id != null
  );

  if (loadingGames) {
    return <div className="card-section text-center p-4"><div className="spinner-border text-secondary" role="status" /></div>;
  }
  if (!games.length) {
    return (
      <div className="card-section">
        <div className="empty-state">
          <p>Search for games using the filters above first</p>
        </div>
      </div>
    );
  }
  if (!casinoGames.length) {
    return (
      <div className="card-section">
        <div className="empty-state">
          <p>No games are added to the casino yet.</p>
          <p style={{ fontSize: 12, color: '#9ca3af' }}>Use the Import Games tab to add games first.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card-section" style={{ padding: 0 }}>
      <div style={{ padding: '14px 20px', borderBottom: '1px solid #e5e7eb' }}>
        <span style={{ fontSize: 13, color: '#374151' }}>
          <strong>{casinoGames.length}</strong> games available for thumbnail import
          {games.length !== casinoGames.length && (
            <span style={{ color: '#9ca3af', marginLeft: 8 }}>
              · {games.length - casinoGames.length} not yet in casino
            </span>
          )}
        </span>
        <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 16 }}>
          Click Thumbnails on any row to expand desktop / mobile sections
        </span>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="table table-sm align-middle" style={{ fontSize: 12, marginBottom: 0 }}>
          <thead>
            <tr>
              <th>Game Name</th>
              <th>Provider Game ID</th>
              <th>BW Game Code</th>
              <th>External Game ID</th>
              <th>Casino Game ID</th>
              <th>Thumbnails</th>
            </tr>
          </thead>
          <tbody>
            {casinoGames.map(g => (
              <ThumbnailGameRow
                key={g.operatorGameConfigId}
                game={g}
                aggId={aggId}
                casinoId={casinoId}
                setLoggedIn={setLoggedIn}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ROOT — CasinoGamesPage
═══════════════════════════════════════════════════════════════ */
const TABS = [
  { key: 'browse',     label: 'Browse Games',      role: null },
  { key: 'import',     label: 'Import Games',       role: 'gis-admin' },
  { key: 'thumbnails', label: 'Import Thumbnails',  role: 'gis-admin' },
];

function CasinoGamesPage({ setLoggedIn }) {
  const [activeTab, setActiveTab] = useState('browse');

  const roles = getWithExpiry('roles') ?? [];
  const isGisAdmin = Array.isArray(roles)
    ? roles.includes('gis-admin')
    : String(roles).includes('gis-admin');

  const visibleTabs = TABS.filter(t => !t.role || isGisAdmin);

  const [aggregators,     setAggregators]     = useState([]);
  const [gisProviders,    setGisProviders]     = useState([]);
  const [enabledCasinos,  setEnabledCasinos]   = useState([]);
  const [casinoProviders, setCasinoProviders]  = useState([]);
  const [jurisdictions,   setJurisdictions]    = useState([]);
  const [categories,      setCategories]       = useState([]);
  const [integrators,     setIntegrators]      = useState([]);
  const [bonusTags,       setBonusTags]        = useState({});

  const [loadingAggs,            setLoadingAggs]            = useState(false);
  const [loadingGisProviders,    setLoadingGisProviders]    = useState(false);
  const [loadingCasinos,         setLoadingCasinos]         = useState(false);
  const [loadingCasinoProviders, setLoadingCasinoProviders] = useState(false);
  const [loadingGames,           setLoadingGames]           = useState(false);

  const [aggId,             setAggId]             = useState('');
  const [providerId,        setProviderId]        = useState('');
  const [apiProviderName,   setApiProviderName]   = useState('');
  const [casinoId,          setCasinoId]          = useState('');
  const [casinoProviderKey, setCasinoProviderKey] = useState('');
  const [strictNameMatch,   setStrictNameMatch]   = useState(true);

  const [games, setGames] = useState([]);

  const tk = () => getWithExpiry('token');

  useEffect(() => {
    const t = tk(); if (!t) { setLoggedIn(false); return; }
    const cached = cacheGet('aggregators');
    if (cached) { setAggregators(cached); return; }
    setLoadingAggs(true);
    getAggregators(t)
      .then(d => { if (d) { setAggregators(d); cacheSet('aggregators', d); }})
      .finally(() => setLoadingAggs(false));
  }, []);

  useEffect(() => {
    const t = tk(); if (!t) return;
    const cached = cacheGet('enabled-casinos');
    if (cached) { setEnabledCasinos(cached); return; }
    setLoadingCasinos(true);
    getEnabledCasinos(t)
      .then(d => { if (d) { setEnabledCasinos(d); cacheSet('enabled-casinos', d); }})
      .finally(() => setLoadingCasinos(false));
  }, []);

  useEffect(() => {
    if (!aggId) { setGisProviders([]); setProviderId(''); setApiProviderName(''); return; }
    const t = tk(); if (!t) return;
    const key = `gis-providers-${aggId}`;
    const cached = cacheGet(key);
    setProviderId(''); setApiProviderName('');
    if (cached) { setGisProviders(cached); return; }
    setLoadingGisProviders(true);
    setGisProviders([]);
    getDbProviders(t, aggId)
      .then(d => {
        const providers = d ?? [];
        setGisProviders(providers); cacheSet(key, providers);
      })
      .finally(() => setLoadingGisProviders(false));
  }, [aggId]);

  const handleProviderPairChange = (newProviderId, newApiProviderName) => {
    setProviderId(newProviderId);
    setApiProviderName(newApiProviderName);
    setGames([]);
  };

  useEffect(() => {
    setCasinoProviderKey('');
    setBonusTags({});
    if (!casinoId) { setCasinoProviders([]); return; }
    const casino = enabledCasinos.find(c => String(c.id) === String(casinoId));
    const brand  = casino?.brand;
    if (!brand) return;
    const t = tk(); if (!t) return;

    const cpKey = `casino-providers-${brand}`;
    const cpCached = cacheGet(cpKey);
    if (cpCached) { setCasinoProviders(cpCached); }
    else {
      setLoadingCasinoProviders(true);
      getCasinoProviders(t, brand)
        .then(d => { if (d) { setCasinoProviders(d); cacheSet(cpKey, d); }})
        .finally(() => setLoadingCasinoProviders(false));
    }

    const catKey = `categories-${brand}`;
    const catCached = cacheGet(catKey);
    if (catCached) { setCategories(catCached); }
    else {
      getCasinoCategories(t, brand)
        .then(d => { if (d) { setCategories(d); cacheSet(catKey, d); }});
    }

    const jKey = `jurisdictions-${brand}`;
    const jCached = cacheGet(jKey);
    if (jCached) { setJurisdictions(jCached); }
    else {
      getCasinoJurisdictions(t, brand)
        .then(d => { if (d) { setJurisdictions(d); cacheSet(jKey, d); }});
    }

    const iKey = `integrators-${brand}`;
    const iCached = cacheGet(iKey);
    if (iCached) { setIntegrators(iCached); }
    else {
      getCasinoIntegrators(t, brand)
        .then(d => { if (d) { setIntegrators(d); cacheSet(iKey, d); }});
    }
    
    getCategoryBonusTags(t, brand)
      .then(d => setBonusTags(d ?? {}))
      .catch(() => toast.warn('Could not load category bonus tags — tags will not auto-fill'));
  }, [casinoId, enabledCasinos]);

  const selectedGisProvider = gisProviders.find(p => String(p.id) === String(providerId));
  const filteredCasinoProviders = strictNameMatch && selectedGisProvider
    ? casinoProviders.filter(cp =>
        cp.title.toLowerCase() === selectedGisProvider.name.toLowerCase() ||
        cp.key.toLowerCase()   === selectedGisProvider.name.toLowerCase()
      )
    : casinoProviders;

  const selectedCasinoProvider = casinoProviders.find(cp => cp.key === casinoProviderKey);
  const namesMismatch = selectedGisProvider && selectedCasinoProvider && (
    selectedCasinoProvider.title.toLowerCase() !== selectedGisProvider.name.toLowerCase() &&
    selectedCasinoProvider.key.toLowerCase()   !== selectedGisProvider.name.toLowerCase()
  );

  const handleSearch = async () => {
    if (!aggId || !providerId || !apiProviderName || !casinoId || !casinoProviderKey) {
      toast.warn('Please fill in all filters'); return;
    }
    const t = tk(); if (!t) { setLoggedIn(false); return; }
    setLoadingGames(true); setGames([]);
    try {
      const result = await getCasinoGameList(t, { aggregatorId: aggId, providerId, apiProviderName, casinoId, casinoProviderKey });
      if (result) { setGames(result); }
    } catch { toast.error('Failed to load games'); }
    finally { setLoadingGames(false); }
  };

  const selectedCasino = enabledCasinos.find(c => String(c.id) === String(casinoId));

  const filterBarProps = {
    aggregators, gisProviders, enabledCasinos, casinoProviders, filteredCasinoProviders,
    loadingAggs, loadingGisProviders, loadingCasinos, loadingCasinoProviders,
    aggId, setAggId: v => { setAggId(v); setGames([]); },
    providerId, apiProviderName, onProviderPairChange: handleProviderPairChange,
    casinoId, setCasinoId: v => { setCasinoId(v); setGames([]); },
    casinoProviderKey, setCasinoProviderKey,
    strictNameMatch, setStrictNameMatch, namesMismatch,
    onSearch: handleSearch, loadingGames,
    token: tk(), setLoggedIn,
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h4 className="page-title">Casino Games</h4>
          <p className="page-subtitle">Browse and import games to the casino wallet</p>
        </div>
      </div>

      <FilterBar {...filterBarProps} />

      <ul className="nav nav-tabs mb-3">
        {visibleTabs.map(t => (
          <li className="nav-item" key={t.key}>
            <button className={`nav-link ${activeTab === t.key ? 'active' : ''}`}
              style={{ border:'none', background:'none', fontFamily:'Lato, sans-serif' }}
              onClick={() => setActiveTab(t.key)}>
              {t.label}
            </button>
          </li>
        ))}
      </ul>

      <div style={{ display: activeTab === 'browse' ? 'block' : 'none' }}>
        <BrowseTab games={games} loadingGames={loadingGames} selectedCasino={selectedCasino} />
      </div>
      {isGisAdmin && <div style={{ display: activeTab === 'import' ? 'block' : 'none' }}>
        <ImportTab
          games={games} loadingGames={loadingGames}
          casinoId={casinoId} casinoProviderKey={casinoProviderKey}
          jurisdictions={jurisdictions} integrators={integrators} categories={categories}
          bonusTags={bonusTags}
          enabledCasinos={enabledCasinos} setLoggedIn={setLoggedIn}
          onReload={handleSearch}
          aggId={aggId} providerId={providerId} apiProviderName={apiProviderName}
          dbProviderName={selectedGisProvider?.name}
        />
      </div>}
      {isGisAdmin && <div style={{ display: activeTab === 'thumbnails' ? 'block' : 'none' }}>
        <ImportThumbnailsTab games={games} loadingGames={loadingGames} aggId={aggId} casinoId={casinoId} setLoggedIn={setLoggedIn} />
      </div>}
    </div>
  );
}

export default CasinoGamesPage;
